import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import Lottie from 'lottie-react';
import {
  Check,
  ChevronDown,
  ExternalLink,
  Eye,
  FilePlus2,
  ImagePlus,
  Loader2,
  MoreVertical,
  Plus,
  Save,
  Search,
  Trash2,
} from 'lucide-react';
import styled, { keyframes } from 'styled-components';
import catPlayingAnimation from '../../assets/catPlaying.json';
import BaseCard from '../../components/Card';
import PublicationBlockEditor from '../../features/publications/PublicationBlockEditor';
import {
  createPublicationBlock,
  createPublicationDraft,
  DEFAULT_PUBLICATION_SEO,
  DEFAULT_PUBLICATION_THEME,
  draftToPayload,
  publicationToDraft,
  slugifyPublicationTitle,
} from '../../features/publications/defaults';
import { uploadPublicationImage } from '../../features/publications/storage';
import {
  getPublicationCategories,
  getPublicationSubcategories,
} from '../../features/publications/taxonomy';
import type {
  PublicationBuilderBlock,
  PublicationBuilderDraft,
  PublicationStatus,
} from '../../features/publications/types';
import {
  useAdminPublication,
  useAdminPublications,
  useCreatePublication,
  useDeletePublication,
  usePublicationAuthors,
  useUpdatePublication,
} from '../../hooks/usePublications';
import { useAuth } from '../../context/AuthProvider';

type SelectedPublication = string | 'new' | null;
type BuilderTabKey = 'overview' | 'media' | 'content' | 'settings';

const STATUS_OPTIONS: Array<{ value: PublicationStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'review', label: 'Review' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

const BLOCK_TYPE_OPTIONS: Array<{ type: PublicationBuilderBlock['type']; label: string }> = [
  { type: 'heading', label: 'Heading' },
  { type: 'paragraph', label: 'Paragraph' },
  { type: 'image', label: 'Image' },
  { type: 'callout', label: 'Callout' },
  { type: 'pullQuote', label: 'Pull quote' },
  { type: 'divider', label: 'Divider' },
  { type: 'list', label: 'List' },
  { type: 'internalCta', label: 'Internal CTA' },
];

const Page = styled.section`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  align-content: start;
  gap: 12px;
  min-width: 0;
  min-height: calc(100dvh - 2.5rem);
  height: calc(100dvh - 2.5rem);
  padding: 0;
  position: relative;

  @supports not (height: 100dvh) {
    height: calc(100vh - 2.5rem);
  }
`;

const Header = styled.div`
  display: grid;
  gap: 4px;
  padding: 12px;
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const HeaderCopy = styled.div`
  display: grid;
  gap: 4px;
  max-width: 980px;
`;

const Eyebrow = styled.span`
  color: #9aa4b2;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.28px;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 0;
  color: #e8eaed;
  font-size: clamp(1.5rem, 2.2vw, 2rem);
  line-height: 1;
  font-weight: 900;
`;

const Subtitle = styled.p`
  margin: 6px 0 0;
  color: #9aa4b2;
  font-size: 12px;
  line-height: 1.5;
  font-weight: 700;
`;

const ToastRail = styled.div`
  position: fixed;
  top: max(16px, env(safe-area-inset-top));
  left: 50%;
  transform: translateX(-50%);
  display: grid;
  justify-items: center;
  padding: 0 12px;
  z-index: 80;
  pointer-events: none;
`;

const TopActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  align-self: center;
`;

const Workspace = styled.div`
  display: grid;
  min-width: 0;
  min-height: 0;
  height: 100%;
  grid-template-columns: clamp(280px, 24vw, 360px) minmax(0, 1fr);
  gap: 12px;

  @media (max-width: 1120px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

const Panel = styled(BaseCard)`
  padding: 0;
  min-height: 0;
  height: 100%;
  display: grid;
  overflow: hidden;
`;

const LibraryPanel = styled(Panel)`
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr) auto;
`;

const BuilderPanel = styled(Panel)`
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
`;

const PanelSection = styled.div`
  padding: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const PanelTitle = styled.h2`
  margin: 0;
  color: #e8eaed;
  font-size: 12px;
  line-height: 1.2;
  font-weight: 900;
  letter-spacing: 0.3px;
  text-transform: uppercase;
`;

const PanelText = styled.p`
  margin: 2px 0 0;
  color: #9aa4b2;
  font-size: 11px;
  line-height: 1.45;
  font-weight: 700;
`;

const SearchWrap = styled.label`
  position: relative;
  display: block;
`;

const SearchIconWrap = styled.span`
  position: absolute;
  top: 50%;
  left: 0.95rem;
  transform: translateY(-50%);
  color: rgba(218, 222, 231, 0.42);
`;

const inputBase = `
  width: 100%;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  color: #f5f7fb;
  font: inherit;
  outline: none;
  transition: border-color 140ms ease, box-shadow 140ms ease, background 140ms ease;

  &::placeholder {
    color: rgba(218, 222, 231, 0.36);
  }

  &:focus {
    border-color: rgba(224, 161, 94, 0.42);
    background: rgba(255, 255, 255, 0.05);
    box-shadow: 0 0 0 3px rgba(224, 161, 94, 0.12);
  }
`;

const SearchInput = styled.input`
  ${inputBase}
  min-height: 2.95rem;
  padding: 0.78rem 0.95rem 0.78rem 2.8rem;
`;

const Select = styled.select`
  ${inputBase}
  min-height: 2.95rem;
  padding: 0.78rem 0.95rem;
`;

const Input = styled.input`
  ${inputBase}
  min-height: 2.95rem;
  padding: 0.78rem 0.95rem;
`;

const Textarea = styled.textarea`
  ${inputBase}
  min-height: 7.4rem;
  padding: 0.88rem 0.95rem;
  resize: vertical;
`;

const FiltersGrid = styled.div`
  display: grid;
  gap: 0.8rem;
`;

const PublicationsList = styled.div`
  min-height: 0;
  overflow-y: auto;
  display: grid;
  gap: 8px;
  padding: 8px 12px 12px;
  align-content: start;
`;

const PublicationRow = styled.button<{ $active?: boolean }>`
  display: grid;
  gap: 10px;
  width: 100%;
  padding: 10px;
  border-radius: 12px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.34)' : 'rgba(255, 255, 255, 0.08)')};
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.1)' : 'rgba(255, 255, 255, 0.02)')};
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 140ms ease, background 140ms ease, transform 140ms ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(224, 161, 94, 0.26);
  }
`;

const PublicationRowTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`;

const PublicationRowTitle = styled.span`
  color: #f3f4f6;
  font-size: 13px;
  font-weight: 900;
  line-height: 1.2;
`;

const PublicationRowMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex-wrap: wrap;
  color: #9aa4b2;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.35;
`;

const PublicationAuthorMeta = styled(PublicationRowMeta)`
  gap: 0.5rem;
`;

const Avatar = styled.span<{ $src?: string | null; $size?: number }>`
  width: ${({ $size = 22 }) => `${$size}px`};
  height: ${({ $size = 22 }) => `${$size}px`};
  min-width: ${({ $size = 22 }) => `${$size}px`};
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #f5f7fb;
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const StatusPill = styled.span<{ $status: PublicationStatus }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 1.55rem;
  padding: 0 0.6rem;
  border-radius: 999px;
  border: 1px solid
    ${({ $status }) =>
      $status === 'published'
        ? 'rgba(101, 137, 99, 0.34)'
        : $status === 'review'
        ? 'rgba(224, 161, 94, 0.34)'
        : $status === 'archived'
        ? 'rgba(150, 150, 150, 0.28)'
        : 'rgba(255, 255, 255, 0.14)'};
  background:
    ${({ $status }) =>
      $status === 'published'
        ? 'rgba(101, 137, 99, 0.16)'
        : $status === 'review'
        ? 'rgba(224, 161, 94, 0.16)'
        : $status === 'archived'
        ? 'rgba(150, 150, 150, 0.12)'
        : 'rgba(255, 255, 255, 0.06)'};
  color:
    ${({ $status }) =>
      $status === 'published'
        ? '#9bc499'
        : $status === 'review'
        ? '#f2c792'
        : $status === 'archived'
        ? '#c8c8c8'
        : '#f5f7fb'};
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const LibraryFooter = styled.div`
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const FooterMeta = styled.span`
  color: rgba(218, 222, 231, 0.58);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SmallButton = styled.button<{ $danger?: boolean }>`
  min-height: 2.55rem;
  padding: 0 0.9rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: 12px;
  border: 1px solid ${({ $danger }) => ($danger ? 'rgba(166, 76, 68, 0.3)' : 'rgba(255, 255, 255, 0.12)')};
  background: ${({ $danger }) => ($danger ? 'rgba(166, 76, 68, 0.14)' : 'rgba(255, 255, 255, 0.04)')};
  color: ${({ $danger }) => ($danger ? '#f6b1ab' : '#f5f7fb')};
  font: inherit;
  font-size: 0.84rem;
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  min-height: 2.8rem;
  padding: 0 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  border-radius: 12px;
  border: 1px solid rgba(224, 161, 94, 0.34);
  background: rgba(224, 161, 94, 0.14);
  color: #f5f7fb;
  font: inherit;
  font-size: 0.84rem;
  font-weight: 900;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const PrimaryIconButton = styled.button`
  width: 2.8rem;
  min-width: 2.8rem;
  height: 2.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  border: 1px solid rgba(224, 161, 94, 0.34);
  background: rgba(224, 161, 94, 0.14);
  color: #f5f7fb;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const BuilderHeader = styled.div`
  display: grid;
  gap: 12px;
  padding: 14px 16px 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const BuilderHeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const BuilderHeaderCopy = styled.div`
  display: grid;
  gap: 4px;
`;

const BuilderHeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const BuilderTitle = styled.h2`
  margin: 0;
  color: #f5f7fb;
  font-size: 1.04rem;
  line-height: 1.3;
  font-weight: 900;
`;

const BuilderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  color: rgba(218, 222, 231, 0.6);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const BuilderMenuAnchor = styled.div`
  position: relative;
`;

const BuilderMenuButton = styled.button`
  width: 2.8rem;
  min-width: 2.8rem;
  height: 2.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: #f5f7fb;
  cursor: pointer;

  &:hover {
    border-color: rgba(255, 255, 255, 0.18);
    background: rgba(255, 255, 255, 0.06);
  }
`;

const BuilderMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 148px;
  display: grid;
  gap: 4px;
  padding: 6px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #15181d;
  box-shadow: 0 18px 36px rgba(0, 0, 0, 0.34);
  z-index: 6;
`;

const BuilderMenuItem = styled.button<{ $danger?: boolean }>`
  min-height: 36px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${({ $danger }) => ($danger ? '#f6b1ab' : '#f5f7fb')};
  font: inherit;
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    background: ${({ $danger }) =>
      $danger ? 'rgba(166, 76, 68, 0.18)' : 'rgba(255, 255, 255, 0.05)'};
  }
`;

const BuilderTabs = styled.div`
  display: flex;
  gap: 6px;
  overflow: auto;
  padding: 8px 16px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const BuilderTabButton = styled.button<{ $active?: boolean }>`
  min-height: 36px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px 10px 0 0;
  border-top: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.34)' : 'rgba(255, 255, 255, 0.08)')};
  border-left: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.34)' : 'rgba(255, 255, 255, 0.08)')};
  border-right: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.34)' : 'rgba(255, 255, 255, 0.08)')};
  border-bottom: 1px solid transparent;
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.1)' : 'rgba(255, 255, 255, 0.02)')};
  color: #e8eaed;
  font: inherit;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  transition: border-color 140ms ease, background 140ms ease, color 140ms ease;
  white-space: nowrap;

  &:hover {
    border-color: rgba(224, 161, 94, 0.24);
    color: #e8eaed;
  }
`;

const BuilderBody = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding: 14px 16px 18px;
  display: grid;
  gap: 14px;
  align-content: start;
`;

const FormSection = styled.section`
  display: grid;
  gap: 0.9rem;
  padding: 1rem;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
`;

const FormSectionTitle = styled.h3`
  margin: 0;
  color: #f5f7fb;
  font-size: 0.96rem;
  line-height: 1.2;
  font-weight: 900;
`;

const FormSectionText = styled.p`
  margin: 0;
  color: rgba(218, 222, 231, 0.62);
  font-size: 0.82rem;
  line-height: 1.6;
  font-weight: 700;
`;

const FormSectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FormSectionHeaderCopy = styled.div`
  display: grid;
  gap: 0.35rem;
  min-width: 0;
  flex: 1 1 22rem;
`;

const FormSectionHeaderAside = styled.div`
  width: min(100%, 15.5rem);
  min-width: 13.5rem;

  @media (max-width: 860px) {
    width: 100%;
    min-width: 0;
  }
`;

const Grid2 = styled.div`
  display: grid;
  gap: 0.85rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const Grid3 = styled.div`
  display: grid;
  gap: 0.85rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

const FieldLabel = styled.label`
  display: grid;
  gap: 0.42rem;
`;

const FieldText = styled.span`
  color: rgba(218, 222, 231, 0.84);
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Helper = styled.span`
  color: rgba(218, 222, 231, 0.46);
  font-size: 0.76rem;
  line-height: 1.55;
`;

const AuthorPickerAnchor = styled.div`
  position: relative;
`;

const AuthorPickerButton = styled.button`
  width: 100%;
  min-height: 2.45rem;
  padding: 0.45rem 0.6rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  color: #f5f7fb;
  cursor: pointer;
  text-align: left;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const AuthorPickerCaret = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(218, 222, 231, 0.64);
`;

const AuthorPickerMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  max-height: 18rem;
  overflow-y: auto;
  display: grid;
  gap: 4px;
  padding: 6px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #15181d;
  box-shadow: 0 18px 36px rgba(0, 0, 0, 0.34);
  z-index: 6;
`;

const AuthorOptionButton = styled.button<{ $active?: boolean }>`
  min-height: 2.85rem;
  width: 100%;
  padding: 0.7rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border: 0;
  border-radius: 10px;
  background: ${({ $active }) =>
    $active ? 'rgba(224, 161, 94, 0.14)' : 'transparent'};
  color: #f5f7fb;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const AuthorOptionCheck = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #e0a15e;
`;

const AuthorIdentityCard = styled.div`
  min-height: 2rem;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
`;

const AuthorIdentityCopy = styled.div`
  display: grid;
  gap: 2px;
  min-width: 0;
`;

const AuthorIdentityName = styled.span`
  color: #f5f7fb;
  font-size: 0.82rem;
  font-weight: 800;
  line-height: 1.25;
`;

const InlineFieldStack = styled.div`
  display: grid;
  gap: 0.65rem;
`;

const ToggleRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  color: #f5f7fb;
  font-size: 0.9rem;
  font-weight: 800;
`;

const Toggle = styled.input`
  width: 1.05rem;
  height: 1.05rem;
`;

const BlockToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

const BlockAddButton = styled.button`
  min-height: 2.5rem;
  padding: 0 0.8rem;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: #f5f7fb;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;
`;

const BlocksStack = styled.div`
  display: grid;
  gap: 0.9rem;
`;

const Details = styled.details`
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.02);
  overflow: hidden;
`;

const DetailsSummary = styled.summary`
  padding: 0.95rem 1rem;
  cursor: pointer;
  color: #f5f7fb;
  font-size: 0.88rem;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const DetailsBody = styled.div`
  padding: 0 1rem 1rem;
  display: grid;
  gap: 0.9rem;
`;

const UploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const UploadButton = styled.button`
  min-height: 2.7rem;
  padding: 0 0.95rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 12px;
  border: 1px solid rgba(224, 161, 94, 0.32);
  background: rgba(224, 161, 94, 0.1);
  color: #f5f7fb;
  font: inherit;
  font-size: 0.84rem;
  font-weight: 800;
  cursor: pointer;
`;

const CoverPreview = styled.img`
  width: 100%;
  max-height: 320px;
  object-fit: contain;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.04);
`;

const Message = styled.div<{ $tone: 'neutral' | 'error' | 'success' }>`
  padding: 0.9rem 1rem;
  border-radius: 14px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'error'
        ? 'rgba(166, 76, 68, 0.32)'
        : $tone === 'success'
        ? 'rgba(101, 137, 99, 0.32)'
        : 'rgba(255, 255, 255, 0.08)'};
  background:
    ${({ $tone }) =>
      $tone === 'error'
        ? 'rgba(166, 76, 68, 0.12)'
        : $tone === 'success'
        ? 'rgba(101, 137, 99, 0.12)'
        : 'rgba(255, 255, 255, 0.03)'};
  color:
    ${({ $tone }) =>
      $tone === 'error'
        ? '#f6b1ab'
        : $tone === 'success'
        ? '#b9d5b7'
        : '#f5f7fb'};
  font-size: 0.84rem;
  line-height: 1.6;
  font-weight: 800;
`;

const PageToast = styled(Message)`
  width: min(100%, 420px);
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  pointer-events: auto;
`;

const EmptyBuilder = styled.div`
  min-height: 280px;
  display: grid;
  place-items: center;
  color: rgba(218, 222, 231, 0.6);
  text-align: center;
  padding: 1.5rem;
`;

const EmptyBuilderState = styled.div`
  grid-row: 1 / -1;
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  padding: 1.5rem;
`;

const EmptyBuilderInner = styled.div`
  width: min(100%, 520px);
  display: grid;
  align-content: center;
  justify-items: center;
  gap: 16px;
  text-align: center;
`;

const EmptyBuilderLottie = styled(Lottie)`
  width: min(100%, 480px);
  height: auto;
`;

const EmptyBuilderTitle = styled.h3`
  margin: 0;
  color: #f5f7fb;
  font-size: 1.1rem;
  line-height: 1.2;
  font-weight: 900;
`;

const EmptyBuilderText = styled.p`
  margin: 0;
  color: rgba(218, 222, 231, 0.62);
  font-size: 0.86rem;
  line-height: 1.6;
  font-weight: 700;
`;

const SpinnerWrap = styled.div`
  min-height: 180px;
  display: grid;
  place-items: center;
  color: rgba(218, 222, 231, 0.7);
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const SpinningLoader = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
`;

function formatDate(dateValue?: string | null) {
  if (!dateValue) return 'Unscheduled';
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return 'Unscheduled';
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTimeLocal(value?: string | null) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function parseDateTimeLocal(value: string) {
  if (!value.trim()) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}

function getInitials(value?: string | null) {
  const normalized = typeof value === 'string' ? value.trim() : '';
  if (!normalized) return 'CB';
  const parts = normalized.split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('') || 'CB';
}

export default function PublicationsPage() {
  const { user, refreshRole } = useAuth();
  const coverInputRef = useRef<HTMLInputElement | null>(null);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);
  const authorPickerRef = useRef<HTMLDivElement | null>(null);
  const [selectedPublication, setSelectedPublication] = useState<SelectedPublication>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PublicationStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(0);
  const [draft, setDraft] = useState<PublicationBuilderDraft>(() => createPublicationDraft());
  const [draftKey, setDraftKey] = useState('new');
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState<{ tone: 'neutral' | 'error' | 'success'; text: string } | null>(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [activeTab, setActiveTab] = useState<BuilderTabKey>('overview');
  const [actionsOpen, setActionsOpen] = useState(false);
  const [authorPickerOpen, setAuthorPickerOpen] = useState(false);

  const publicationsQuery = useAdminPublications({
    q: search,
    status: statusFilter,
    category: categoryFilter,
    page,
  });
  const publicationQuery = useAdminPublication(
    selectedPublication && selectedPublication !== 'new' ? selectedPublication : null,
  );
  const createPublication = useCreatePublication();
  const updatePublication = useUpdatePublication();
  const deletePublication = useDeletePublication();
  const publicationAuthors = usePublicationAuthors();

  const publications = useMemo(
    () => publicationsQuery.data?.publications ?? [],
    [publicationsQuery.data?.publications],
  );
  const total = publicationsQuery.data?.total ?? 0;
  const pageSize = publicationsQuery.data?.pageSize ?? 20;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const categories = useMemo(
    () => getPublicationCategories(publications.map((publication) => publication.category)),
    [publications],
  );
  const subcategories = useMemo(
    () => getPublicationSubcategories(draft.category, draft.subcategory ? [draft.subcategory] : []),
    [draft.category, draft.subcategory],
  );
  const authorOptions = useMemo(() => {
    const options = [...(publicationAuthors.data ?? [])];
    options.sort((left, right) => {
      const leftIsCurrent = left.firebaseUid === user?.uid;
      const rightIsCurrent = right.firebaseUid === user?.uid;
      if (leftIsCurrent && !rightIsCurrent) return -1;
      if (!leftIsCurrent && rightIsCurrent) return 1;
      return left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });
    });
    return options;
  }, [publicationAuthors.data, user?.uid]);
  const selectedAuthor = useMemo(
    () =>
      authorOptions.find((option) => option.firebaseUid === draft.authorFirebaseUid) ??
      null,
    [authorOptions, draft.authorFirebaseUid],
  );

  useEffect(() => {
    if (selectedPublication === 'new') {
      setDraft(createPublicationDraft());
      setDraftKey('new');
      setDirty(false);
      return;
    }

    if (!publicationQuery.data || selectedPublication !== publicationQuery.data.id) return;
    const nextKey = `${publicationQuery.data.id}:${publicationQuery.data.updatedAt}`;
    if (draftKey === nextKey) return;
    setDraft(publicationToDraft(publicationQuery.data));
    setDraftKey(nextKey);
    setDirty(false);
  }, [draftKey, publicationQuery.data, selectedPublication]);

  useEffect(() => {
    if (page >= totalPages) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);

  useEffect(() => {
    if (!actionsOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (actionsMenuRef.current?.contains(target)) return;
      setActionsOpen(false);
    };

    window.addEventListener('mousedown', handlePointerDown);
    return () => window.removeEventListener('mousedown', handlePointerDown);
  }, [actionsOpen]);

  useEffect(() => {
    if (!authorPickerOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (authorPickerRef.current?.contains(target)) return;
      setAuthorPickerOpen(false);
    };

    window.addEventListener('mousedown', handlePointerDown);
    return () => window.removeEventListener('mousedown', handlePointerDown);
  }, [authorPickerOpen]);

  useEffect(() => {
    if (!selectedPublication || selectedPublication !== 'new') return;
    if (!authorOptions.length) return;
    if (draft.authorFirebaseUid) return;

    const currentAuthor = authorOptions.find((option) => option.firebaseUid === user?.uid) ?? authorOptions[0];
    if (!currentAuthor) return;

    setDraft((current) => ({
      ...current,
      authorFirebaseUid: currentAuthor.firebaseUid,
      authorName: currentAuthor.name,
      authorAvatarUrl: currentAuthor.photoURL,
    }));
  }, [authorOptions, draft.authorFirebaseUid, selectedPublication, user?.uid]);

  useEffect(() => {
    if (!message) return;
    const timeoutId = window.setTimeout(() => {
      setMessage(null);
    }, 10000);

    return () => window.clearTimeout(timeoutId);
  }, [message]);

  const setDraftField = <Key extends keyof PublicationBuilderDraft>(field: Key, value: PublicationBuilderDraft[Key]) => {
    setDraft((current) => ({
      ...current,
      [field]: value,
    }));
    setDirty(true);
  };

  const handleCategoryChange = (nextCategory: string) => {
    setDraft((current) => {
      const nextSubcategories = getPublicationSubcategories(
        nextCategory,
        current.subcategory ? [current.subcategory] : [],
      );

      return {
        ...current,
        category: nextCategory,
        subcategory:
          current.subcategory && nextSubcategories.includes(current.subcategory)
            ? current.subcategory
            : '',
      };
    });
    setDirty(true);
  };

  const handleAuthorChange = (authorFirebaseUid: string) => {
    const nextAuthor =
      authorOptions.find((option) => option.firebaseUid === authorFirebaseUid) ?? null;
    setDraft((current) => ({
      ...current,
      authorFirebaseUid,
      authorName: nextAuthor?.name ?? '',
      authorAvatarUrl: nextAuthor?.photoURL ?? '',
    }));
    setDirty(true);
    setAuthorPickerOpen(false);
  };

  const updateBlock = (blockId: string, next: PublicationBuilderBlock) => {
    setDraft((current) => ({
      ...current,
      blocks: current.blocks.map((block) => (block.id === blockId ? next : block)),
    }));
    setDirty(true);
  };

  const handleKeyPointChange = (index: number, value: string) => {
    setDraft((current) => {
      const nextKeyPoints = [...current.keyPoints];
      const trimmedValue = value.trim();

      if (trimmedValue) {
        nextKeyPoints[index] = trimmedValue;
      } else {
        nextKeyPoints.splice(index, 1);
      }

      return {
        ...current,
        keyPoints: nextKeyPoints,
      };
    });
    setDirty(true);
  };

  const moveBlock = (blockId: string, direction: -1 | 1) => {
    setDraft((current) => {
      const index = current.blocks.findIndex((block) => block.id === blockId);
      if (index === -1) return current;
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= current.blocks.length) return current;
      const nextBlocks = [...current.blocks];
      const [item] = nextBlocks.splice(index, 1);
      nextBlocks.splice(targetIndex, 0, item);
      return {
        ...current,
        blocks: nextBlocks,
      };
    });
    setDirty(true);
  };

  const deleteBlock = (blockId: string) => {
    setDraft((current) => {
      const nextBlocks = current.blocks.filter((block) => block.id !== blockId);
      return {
        ...current,
        blocks: nextBlocks.length ? nextBlocks : [createPublicationBlock('paragraph')],
      };
    });
    setDirty(true);
  };

  const addBlock = (type: PublicationBuilderBlock['type']) => {
    setDraft((current) => ({
      ...current,
      blocks: [...current.blocks, createPublicationBlock(type)],
    }));
    setDirty(true);
  };

  const handleStartNew = () => {
    setSelectedPublication('new');
    setActiveTab('overview');
    setMessage(null);
    setActionsOpen(false);
    setAuthorPickerOpen(false);
  };

  const handleSelectPublication = (publicationId: string) => {
    setSelectedPublication(publicationId);
    setActiveTab('overview');
    setMessage(null);
    setActionsOpen(false);
    setAuthorPickerOpen(false);
  };

  const handleUploadImage = async (file: File, slot: string) => {
    const slugCandidate = draft.slug || slugifyPublicationTitle(draft.title) || 'draft';
    await refreshRole();
    const upload = await uploadPublicationImage({
      file,
      publicationSlug: slugCandidate,
      userId: user?.uid ?? null,
      slot,
    });
    return upload.downloadUrl;
  };

  const handleCoverUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingCover(true);
    setMessage(null);
    try {
      const url = await handleUploadImage(file, 'cover');
      setDraft((current) => ({
        ...current,
        coverImageUrl: url,
        coverImageAlt: current.coverImageAlt || file.name.replace(/\.[^.]+$/, ''),
        seo: {
          ...DEFAULT_PUBLICATION_SEO,
          ...current.seo,
          imageUrl: current.seo.imageUrl || url,
        },
      }));
      setDirty(true);
    } catch (error) {
      setMessage({
        tone: 'error',
        text: error instanceof Error ? error.message : 'Cover image upload failed.',
      });
    } finally {
      setUploadingCover(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleSave = async () => {
    try {
      setMessage(null);
      const payload = draftToPayload({
        ...draft,
        slug: draft.slug || slugifyPublicationTitle(draft.title),
      });

      if (!payload.title || !payload.category) {
        setMessage({
          tone: 'error',
          text: 'Title and category are required before saving a publication.',
        });
        return;
      }

      const result =
        draft.id
          ? await updatePublication.mutateAsync({
              publicationId: draft.id,
              payload,
            })
          : await createPublication.mutateAsync(payload);

      setSelectedPublication(result.id);
      setDraft(publicationToDraft(result));
      setDraftKey(`${result.id}:${result.updatedAt}`);
      setDirty(false);
      setMessage({
        tone: 'success',
        text: draft.id ? 'Publication updated.' : 'Publication created.',
      });
    } catch (error) {
      setMessage({
        tone: 'error',
        text: error instanceof Error ? error.message : 'Publication save failed.',
      });
    }
  };

  const handleDelete = async () => {
    if (!draft.id) return;
    const confirmed = window.confirm(`Delete "${draft.title || 'this publication'}"?`);
    if (!confirmed) return;

    try {
      await deletePublication.mutateAsync(draft.id);
      setSelectedPublication(null);
      setDraft(createPublicationDraft());
      setDraftKey('new');
      setDirty(false);
      setActionsOpen(false);
      setMessage({
        tone: 'success',
        text: 'Publication deleted.',
      });
    } catch (error) {
      setMessage({
        tone: 'error',
        text: error instanceof Error ? error.message : 'Publication deletion failed.',
      });
    }
  };

  const previewSlug = draft.slug || slugifyPublicationTitle(draft.title);
  const publicationHref = draft.id && previewSlug ? `/publications/${previewSlug}` : null;
  const isBuilderIdle = selectedPublication === null;
  const isSaving = createPublication.isPending || updatePublication.isPending;
  const isBusy =
    isSaving ||
    deletePublication.isPending ||
    uploadingCover;
  const toastPortal =
    message && typeof document !== 'undefined'
      ? createPortal(
          <ToastRail>
            <PageToast $tone={message.tone}>{message.text}</PageToast>
          </ToastRail>,
          document.body,
        )
      : null;

  return (
    <Page>
      {toastPortal}
      <Header>
        <HeaderTop>
          <HeaderCopy>
            <Eyebrow>Editorial Workspace</Eyebrow>
            <Title>Publications</Title>
            <Subtitle>
              Build and manage CyBard publications with the same modular block system used on the
              public blogs page. Creator credit is applied automatically when a publication goes
              live.
            </Subtitle>
          </HeaderCopy>

          <TopActions>
            <PrimaryButton type="button" onClick={handleStartNew}>
              <FilePlus2 size={16} />
              New publication
            </PrimaryButton>
          </TopActions>
        </HeaderTop>
      </Header>

      <Workspace>
        <LibraryPanel>
          <PanelSection>
            <PanelTitle>Publication library</PanelTitle>
            <PanelText>
              Browse drafts, review pieces, and published articles. Select an entry to edit the full
              publication definition.
            </PanelText>
          </PanelSection>

          <PanelSection>
            <FiltersGrid>
              <SearchWrap>
                <SearchIconWrap>
                  <Search size={16} />
                </SearchIconWrap>
                <SearchInput
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(0);
                  }}
                  placeholder="Search publications"
                />
              </SearchWrap>

              <Grid2>
                <Select
                  value={statusFilter}
                  onChange={(event) => {
                    setStatusFilter(event.target.value as PublicationStatus | 'all');
                    setPage(0);
                  }}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>

                <Select
                  value={categoryFilter}
                  onChange={(event) => {
                    setCategoryFilter(event.target.value);
                    setPage(0);
                  }}
                >
                  <option value="">All categories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </Select>
              </Grid2>
            </FiltersGrid>
          </PanelSection>

          <PublicationsList>
            {publicationsQuery.isLoading ? (
              <SpinnerWrap>
                <SpinningLoader size={20} />
              </SpinnerWrap>
            ) : publications.length ? (
              publications.map((publication) => (
                <PublicationRow
                  key={publication.id}
                  type="button"
                  $active={selectedPublication === publication.id}
                  onClick={() => handleSelectPublication(publication.id)}
                >
                  <PublicationRowTop>
                    <PublicationRowTitle>{publication.title}</PublicationRowTitle>
                    <StatusPill $status={publication.status}>{publication.status}</StatusPill>
                  </PublicationRowTop>
                  <PublicationRowMeta>
                    <span>{publication.category}</span>
                    {publication.subcategory ? <span>{publication.subcategory}</span> : null}
                  </PublicationRowMeta>
                  <PublicationAuthorMeta>
                    <Avatar $src={publication.authorAvatarUrl} $size={20}>
                      {publication.authorAvatarUrl ? <img src={publication.authorAvatarUrl} alt={publication.author} /> : getInitials(publication.author)}
                    </Avatar>
                    <span>By {publication.author}</span>
                  </PublicationAuthorMeta>
                  <PublicationRowMeta>
                    <span>Updated {formatDate(publication.updatedAt)}</span>
                  </PublicationRowMeta>
                </PublicationRow>
              ))
            ) : (
              <EmptyBuilder>No publications matched the current filters.</EmptyBuilder>
            )}
          </PublicationsList>

          <LibraryFooter>
            <FooterMeta>
              Page {page + 1} of {totalPages}
            </FooterMeta>
            <FooterActions>
              <SmallButton type="button" onClick={() => setPage((current) => Math.max(0, current - 1))} disabled={page === 0}>
                Prev
              </SmallButton>
              <SmallButton
                type="button"
                onClick={() => setPage((current) => (current + 1 < totalPages ? current + 1 : current))}
                disabled={page + 1 >= totalPages}
              >
                Next
              </SmallButton>
            </FooterActions>
          </LibraryFooter>
        </LibraryPanel>

        <BuilderPanel>
          {isBuilderIdle ? (
            <EmptyBuilderState>
              <EmptyBuilderInner>
                <EmptyBuilderLottie animationData={catPlayingAnimation} loop autoplay />
                <EmptyBuilderTitle>No publication selected</EmptyBuilderTitle>
              <EmptyBuilderText>
                  Start a new publication or choose an existing one from the library to open the
                  builder.
                </EmptyBuilderText>
                <PrimaryButton type="button" onClick={handleStartNew}>
                  <FilePlus2 size={16} />
                  Create new publication
                </PrimaryButton>
              </EmptyBuilderInner>
            </EmptyBuilderState>
          ) : (
            <>
              <BuilderHeader>
                <BuilderHeaderTop>
                  <BuilderHeaderCopy>
                  <BuilderTitle>
                    {draft.id
                      ? draft.title || 'Untitled publication'
                      : 'New publication'}
                  </BuilderTitle>
                  <BuilderMeta>
                    <StatusPill $status={draft.status}>{draft.status}</StatusPill>
                    {dirty ? <span>Unsaved changes</span> : null}
                    {draft.createdByName ? <span>Creator: {draft.createdByName}</span> : null}
                    {draft.updatedByName ? <span>Last editor: {draft.updatedByName}</span> : null}
                    {draft.updatedAt ? <span>Updated {formatDate(draft.updatedAt)}</span> : null}
                  </BuilderMeta>
                  </BuilderHeaderCopy>

                  <BuilderHeaderControls>
                    {dirty || isSaving ? (
                      <PrimaryIconButton
                        type="button"
                        onClick={handleSave}
                        disabled={isBusy}
                        aria-label={isSaving ? 'Saving publication' : 'Save publication'}
                        title={isSaving ? 'Saving publication' : 'Save publication'}
                      >
                        <Save size={15} />
                      </PrimaryIconButton>
                    ) : null}

                    {draft.id ? (
                      <BuilderMenuAnchor ref={actionsMenuRef}>
                        <BuilderMenuButton
                          type="button"
                          aria-haspopup="menu"
                          aria-expanded={actionsOpen}
                          aria-label="Publication actions"
                          title="Publication actions"
                          onClick={() => setActionsOpen((current) => !current)}
                        >
                          <MoreVertical size={15} />
                        </BuilderMenuButton>

                        {actionsOpen ? (
                          <BuilderMenu role="menu" aria-label="Publication actions">
                            {publicationHref ? (
                              <BuilderMenuItem
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                  setActionsOpen(false);
                                  window.open(publicationHref, '_blank', 'noopener,noreferrer');
                                }}
                              >
                                <Eye size={15} />
                                Preview
                              </BuilderMenuItem>
                            ) : null}
                            {publicationHref ? (
                              <BuilderMenuItem
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                  setActionsOpen(false);
                                  window.open(publicationHref, '_blank', 'noopener,noreferrer');
                                }}
                              >
                                <ExternalLink size={15} />
                                Open
                              </BuilderMenuItem>
                            ) : null}
                            <BuilderMenuItem
                              type="button"
                              role="menuitem"
                              $danger
                              onClick={() => {
                                setActionsOpen(false);
                                void handleDelete();
                              }}
                              disabled={isBusy}
                            >
                              <Trash2 size={15} />
                              Delete
                            </BuilderMenuItem>
                          </BuilderMenu>
                        ) : null}
                      </BuilderMenuAnchor>
                    ) : null}
                  </BuilderHeaderControls>
                </BuilderHeaderTop>
              </BuilderHeader>
              <BuilderTabs>
                <BuilderTabButton type="button" $active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
                  Overview
                </BuilderTabButton>
                <BuilderTabButton type="button" $active={activeTab === 'media'} onClick={() => setActiveTab('media')}>
                  Media
                </BuilderTabButton>
                <BuilderTabButton type="button" $active={activeTab === 'content'} onClick={() => setActiveTab('content')}>
                  Content
                </BuilderTabButton>
                <BuilderTabButton type="button" $active={activeTab === 'settings'} onClick={() => setActiveTab('settings')}>
                  Settings
                </BuilderTabButton>
              </BuilderTabs>
              <BuilderBody>
                {publicationQuery.isLoading && selectedPublication !== 'new' ? (
              <SpinnerWrap>
                <SpinningLoader size={20} />
              </SpinnerWrap>
            ) : (
              <>
                {activeTab === 'overview' ? (
                  <>
                <FormSection>
                  <FormSectionHeader>
                    <FormSectionHeaderCopy>
                      <FormSectionTitle>Core details</FormSectionTitle>
                      <FormSectionText>
                        Title, category, slug, and publishing status are the top-level controls used by
                        the archive and public article pages.
                      </FormSectionText>
                    </FormSectionHeaderCopy>
                    <FormSectionHeaderAside>
                      <FieldLabel>
                        <FieldText>Author</FieldText>
                        <AuthorPickerAnchor ref={authorPickerRef}>
                          <AuthorPickerButton
                            type="button"
                            onClick={() => setAuthorPickerOpen((current) => !current)}
                            disabled={publicationAuthors.isLoading || authorOptions.length === 0}
                            aria-haspopup="listbox"
                            aria-expanded={authorPickerOpen}
                            aria-label="Select publication author"
                          >
                            <AuthorIdentityCard>
                              <Avatar $src={selectedAuthor?.photoURL ?? draft.authorAvatarUrl} $size={34}>
                                {selectedAuthor?.photoURL || draft.authorAvatarUrl ? (
                                  <img
                                    src={selectedAuthor?.photoURL ?? draft.authorAvatarUrl ?? undefined}
                                    alt={selectedAuthor?.name ?? draft.authorName ?? 'Publication author'}
                                  />
                                ) : (
                                  getInitials(selectedAuthor?.name ?? draft.authorName)
                                )}
                              </Avatar>
                              <AuthorIdentityCopy>
                                <AuthorIdentityName>
                                  {publicationAuthors.isLoading
                                    ? 'Loading authors...'
                                    : selectedAuthor?.name ?? draft.authorName ?? 'Select author'}
                                </AuthorIdentityName>
                              </AuthorIdentityCopy>
                            </AuthorIdentityCard>
                            <AuthorPickerCaret>
                              <ChevronDown size={16} />
                            </AuthorPickerCaret>
                          </AuthorPickerButton>

                          {authorPickerOpen ? (
                            <AuthorPickerMenu role="listbox" aria-label="Publication authors">
                              {authorOptions.map((author) => {
                                const active = author.firebaseUid === draft.authorFirebaseUid;
                                return (
                                  <AuthorOptionButton
                                    key={author.firebaseUid}
                                    type="button"
                                    role="option"
                                    aria-selected={active}
                                    $active={active}
                                    onClick={() => handleAuthorChange(author.firebaseUid)}
                                  >
                                    <Avatar $src={author.photoURL} $size={36}>
                                      {author.photoURL ? (
                                        <img src={author.photoURL} alt={author.name} />
                                      ) : (
                                        getInitials(author.name)
                                      )}
                                    </Avatar>
                                    <AuthorIdentityCopy>
                                      <AuthorIdentityName>
                                        {author.name}
                                        {author.firebaseUid === user?.uid ? ' (You)' : ''}
                                      </AuthorIdentityName>
                                    </AuthorIdentityCopy>
                                    {active ? (
                                      <AuthorOptionCheck>
                                        <Check size={16} />
                                      </AuthorOptionCheck>
                                    ) : null}
                                  </AuthorOptionButton>
                                );
                              })}
                            </AuthorPickerMenu>
                          ) : null}
                        </AuthorPickerAnchor>
                      </FieldLabel>
                    </FormSectionHeaderAside>
                  </FormSectionHeader>
                  <Grid2>
                    <FieldLabel>
                      <FieldText>Title</FieldText>
                      <Input value={draft.title} onChange={(event) => setDraftField('title', event.target.value)} placeholder="Publication title" />
                    </FieldLabel>
                    <FieldLabel>
                      <FieldText>Slug</FieldText>
                      <Input
                        value={draft.slug}
                        onChange={(event) => setDraftField('slug', event.target.value)}
                        placeholder={slugifyPublicationTitle(draft.title) || 'generated-from-title'}
                      />
                    </FieldLabel>
                  </Grid2>
                  <Grid3>
                    <FieldLabel>
                      <FieldText>Status</FieldText>
                      <Select value={draft.status} onChange={(event) => setDraftField('status', event.target.value as PublicationStatus)}>
                        {STATUS_OPTIONS.filter((option) => option.value !== 'all').map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </FieldLabel>
                    <FieldLabel>
                      <FieldText>Category</FieldText>
                      <Select value={draft.category} onChange={(event) => handleCategoryChange(event.target.value)}>
                        <option value="">Select category</option>
                        {categories.map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </Select>
                    </FieldLabel>
                    <FieldLabel>
                      <FieldText>Subcategory</FieldText>
                      <Select
                        value={draft.subcategory ?? ''}
                        onChange={(event) => setDraftField('subcategory', event.target.value)}
                        disabled={!draft.category}
                      >
                        <option value="">{draft.category ? 'No subcategory' : 'Select category first'}</option>
                        {subcategories.map((subcategory) => (
                          <option key={subcategory} value={subcategory}>
                            {subcategory}
                          </option>
                        ))}
                      </Select>
                    </FieldLabel>
                  </Grid3>
                  <Grid2>
                    <FieldLabel>
                      <FieldText>Subtitle</FieldText>
                      <Textarea
                        value={draft.subtitle}
                        onChange={(event) => setDraftField('subtitle', event.target.value)}
                        placeholder="Short deck or subhead"
                      />
                    </FieldLabel>
                    <FieldLabel>
                      <FieldText>Excerpt</FieldText>
                      <Textarea
                        value={draft.excerpt}
                        onChange={(event) => setDraftField('excerpt', event.target.value)}
                        placeholder="Archive teaser and SEO fallback"
                      />
                    </FieldLabel>
                  </Grid2>
                  <Grid3>
                    <FieldLabel>
                      <FieldText>Read time</FieldText>
                      <Input value={draft.readTime} onChange={(event) => setDraftField('readTime', event.target.value)} placeholder="8 min read" />
                    </FieldLabel>
                    <FieldLabel>
                      <FieldText>Publish date</FieldText>
                      <Input
                        type="datetime-local"
                        value={formatDateTimeLocal(draft.publishedAt)}
                        onChange={(event) => setDraftField('publishedAt', parseDateTimeLocal(event.target.value))}
                      />
                    </FieldLabel>
                    <FieldLabel>
                      <FieldText>Advertising</FieldText>
                      <ToggleRow>
                        <Toggle
                          type="checkbox"
                          checked={draft.advertisingEnabled}
                          onChange={(event) => setDraftField('advertisingEnabled', event.target.checked)}
                        />
                        Enable future ad placement controls
                      </ToggleRow>
                    </FieldLabel>
                  </Grid3>
                </FormSection>
                  </>
                ) : null}

                {activeTab === 'media' ? (
                <FormSection>
                  <FormSectionTitle>Cover image</FormSectionTitle>
                  <FormSectionText>
                    Upload the hero image to Firebase Storage or paste a direct image URL. The same
                    image can also power SEO previews.
                  </FormSectionText>
                  {draft.coverImageUrl ? (
                    <CoverPreview src={draft.coverImageUrl} alt={draft.coverImageAlt || 'Cover preview'} />
                  ) : null}
                  <UploadRow>
                    <UploadButton type="button" onClick={() => coverInputRef.current?.click()} disabled={uploadingCover}>
                      <ImagePlus size={15} />
                      {uploadingCover ? 'Uploading…' : 'Upload cover'}
                    </UploadButton>
                    <Helper>Images upload to Firebase Storage and the download URL is stored on the publication.</Helper>
                    <input
                      ref={coverInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
                      style={{ display: 'none' }}
                      onChange={handleCoverUpload}
                    />
                  </UploadRow>
                  <Grid2>
                    <FieldLabel>
                      <FieldText>Cover image URL</FieldText>
                      <Input
                        value={draft.coverImageUrl ?? ''}
                        onChange={(event) => setDraftField('coverImageUrl', event.target.value)}
                        placeholder="https://..."
                      />
                    </FieldLabel>
                    <FieldLabel>
                      <FieldText>Cover image alt</FieldText>
                      <Input
                        value={draft.coverImageAlt ?? ''}
                        onChange={(event) => setDraftField('coverImageAlt', event.target.value)}
                        placeholder="Describe the cover image"
                      />
                    </FieldLabel>
                  </Grid2>
                </FormSection>
                ) : null}

                {activeTab === 'overview' ? (
                <FormSection>
                  <FormSectionTitle>Key points</FormSectionTitle>
                  <FormSectionText>
                    These show up in the flat key-points rail on the public article page.
                  </FormSectionText>
                  <FieldLabel>
                    <FieldText>Key points</FieldText>
                    <InlineFieldStack>
                      {[...draft.keyPoints, ''].map((point, index, allPoints) => {
                        const isAppendedEmptyRow = index === allPoints.length - 1;
                        return (
                          <Input
                            key={`key-point-${index}`}
                            value={point}
                            onChange={(event) => handleKeyPointChange(index, event.target.value)}
                            placeholder={
                              isAppendedEmptyRow
                                ? 'Add another key point'
                                : `Key point ${index + 1}`
                            }
                          />
                        );
                      })}
                    </InlineFieldStack>
                    <Helper>
                      As soon as you start typing in the last field, a new empty row appears automatically.
                    </Helper>
                  </FieldLabel>
                </FormSection>
                ) : null}

                {activeTab === 'content' ? (
                <FormSection>
                  <FormSectionTitle>Publication blocks</FormSectionTitle>
                  <FormSectionText>
                    Add, reorder, and edit the exact content blocks that render the public article.
                  </FormSectionText>
                  <BlockToolbar>
                    {BLOCK_TYPE_OPTIONS.map((option) => (
                      <BlockAddButton key={option.type} type="button" onClick={() => addBlock(option.type)}>
                        <Plus size={14} />
                        {option.label}
                      </BlockAddButton>
                    ))}
                  </BlockToolbar>
                  <BlocksStack>
                    {draft.blocks.map((block, index) => (
                      <PublicationBlockEditor
                        key={block.id}
                        block={block}
                        index={index}
                        total={draft.blocks.length}
                        onChange={(next) => updateBlock(block.id, next)}
                        onMoveUp={() => moveBlock(block.id, -1)}
                        onMoveDown={() => moveBlock(block.id, 1)}
                        onDelete={() => deleteBlock(block.id)}
                        onUploadImage={handleUploadImage}
                      />
                    ))}
                  </BlocksStack>
                </FormSection>
                ) : null}

                {activeTab === 'settings' ? (
                  <>
                <Details>
                  <DetailsSummary>Theme</DetailsSummary>
                  <DetailsBody>
                    <Grid3>
                      <FieldLabel>
                        <FieldText>Accent</FieldText>
                        <Input
                          value={draft.theme.accent}
                          onChange={(event) =>
                            setDraftField('theme', { ...draft.theme, accent: event.target.value || DEFAULT_PUBLICATION_THEME.accent })
                          }
                        />
                      </FieldLabel>
                      <FieldLabel>
                        <FieldText>Glow</FieldText>
                        <Input
                          value={draft.theme.glow}
                          onChange={(event) =>
                            setDraftField('theme', { ...draft.theme, glow: event.target.value || DEFAULT_PUBLICATION_THEME.glow })
                          }
                        />
                      </FieldLabel>
                      <FieldLabel>
                        <FieldText>Surface</FieldText>
                        <Input
                          value={draft.theme.surface}
                          onChange={(event) =>
                            setDraftField('theme', { ...draft.theme, surface: event.target.value || DEFAULT_PUBLICATION_THEME.surface })
                          }
                        />
                      </FieldLabel>
                      <FieldLabel>
                        <FieldText>Paper</FieldText>
                        <Input
                          value={draft.theme.paper}
                          onChange={(event) =>
                            setDraftField('theme', { ...draft.theme, paper: event.target.value || DEFAULT_PUBLICATION_THEME.paper })
                          }
                        />
                      </FieldLabel>
                      <FieldLabel>
                        <FieldText>Ink</FieldText>
                        <Input
                          value={draft.theme.ink}
                          onChange={(event) =>
                            setDraftField('theme', { ...draft.theme, ink: event.target.value || DEFAULT_PUBLICATION_THEME.ink })
                          }
                        />
                      </FieldLabel>
                      <FieldLabel>
                        <FieldText>Muted ink</FieldText>
                        <Input
                          value={draft.theme.mutedInk}
                          onChange={(event) =>
                            setDraftField('theme', {
                              ...draft.theme,
                              mutedInk: event.target.value || DEFAULT_PUBLICATION_THEME.mutedInk,
                            })
                          }
                        />
                      </FieldLabel>
                    </Grid3>
                  </DetailsBody>
                </Details>

                <Details>
                  <DetailsSummary>SEO</DetailsSummary>
                  <DetailsBody>
                    <Grid2>
                      <FieldLabel>
                        <FieldText>SEO title</FieldText>
                        <Input
                          value={draft.seo.title ?? ''}
                          onChange={(event) => setDraftField('seo', { ...draft.seo, title: event.target.value })}
                        />
                      </FieldLabel>
                      <FieldLabel>
                        <FieldText>SEO image</FieldText>
                        <Input
                          value={draft.seo.imageUrl ?? ''}
                          onChange={(event) => setDraftField('seo', { ...draft.seo, imageUrl: event.target.value })}
                        />
                      </FieldLabel>
                      <FieldLabel style={{ gridColumn: '1 / -1' }}>
                        <FieldText>SEO description</FieldText>
                        <Textarea
                          value={draft.seo.description ?? ''}
                          onChange={(event) => setDraftField('seo', { ...draft.seo, description: event.target.value })}
                        />
                      </FieldLabel>
                    </Grid2>
                  </DetailsBody>
                </Details>
                  </>
                ) : null}
              </>
            )}
              </BuilderBody>
            </>
          )}
        </BuilderPanel>
      </Workspace>
    </Page>
  );
}
